import { useQuery } from '@apollo/client'
import Switch from 'components/atoms/Switch'
import SubscriptionCard from 'components/landing/Subscriptions/SubscriptionCard'
import ReassuranceItems from 'components/ReassuranceItems'
import { DrawerContext } from 'context/Drawer'
import { useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { colors } from 'theme'
import {
  type AvailablePlansQuery,
  type AvailablePlansQueryVariables,
  type MySubscriptionQuery,
} from 'types/graphql'
import { getDiscountModalData, isPharmUppUltra } from 'utils/origin'
import { hasSpecialOffer } from 'utils/plans'

import { AVAILABLE_PLANS } from '@/graphql/queries/plan'
import { SUBSCRIPTION } from '@/graphql/queries/user'

const Subscriptions = () => {
  const { actions } = useContext(DrawerContext)
  const consortiumData = useMemo(() => getDiscountModalData()?.consortium, [])
  const { data: plansData, loading } = useQuery<
    AvailablePlansQuery,
    AvailablePlansQueryVariables
  >(AVAILABLE_PLANS)
  const currentUser = useQuery<MySubscriptionQuery>(SUBSCRIPTION)
  const { setPlan } = actions
  const [value, setValue] = useState(false)

  if (loading || plansData === undefined || currentUser.loading) {
    return null
  }

  const availablePlans = plansData?.availablePlans ?? []

  const hasBanner = availablePlans && hasSpecialOffer(availablePlans)
  const plans = value
    ? availablePlans.filter(plan => plan?.id.includes('TEAM'))
    : availablePlans.filter(plan => !plan?.id.includes('TEAM'))
  const isActiveSub = currentUser?.data?.me?.subscription != null

  return (
    <Wrapper id="subscriptions">
      <Title>Nos abonnements</Title>
      {!isPharmUppUltra() ? (
        <DiscountWrapper>
          {!isActiveSub ? (
            <Discount>
              {`Testez Défimédoc pendant ${consortiumData?.trialPeriod != null ? consortiumData.trialPeriod : '14 jours gratuitement et sans engagement !'}`}
            </Discount>
          ) : (
            <Contact>
              <p>
                Vous êtes déjà abonné à Défimédoc. Rendez-vous dans votre
                profil, onglet{' '}
                <a href="/me/subscription">« Mon abonnement »</a>{' '}
              </p>
            </Contact>
          )}
        </DiscountWrapper>
      ) : null}
      {availablePlans.length > 0 && !isActiveSub ? (
        <>
          <SwitchWrapper>
            <Switch isOn={value} handleToggle={() => setValue(!value)} />
          </SwitchWrapper>
          <SubscriptionsList $hasBanner={hasBanner}>
            {plans.map((plan, key) => (
              <li key={key}>
                <SubscriptionCard
                  {...plan}
                  title={plan?.title ?? ''}
                  interval={plan?.interval ?? ''}
                  metadata={
                    plan?.metadata ?? {
                      displayPrice: '',
                      taxType: '',
                      threshold: 0,
                    }
                  }
                  description={plan?.description ?? ''}
                  onClick={() => setPlan(plan)}
                />
              </li>
            ))}
          </SubscriptionsList>
        </>
      ) : (
        <Contact>
          <span>
            Vous souhaitez augmenter votre nombre de licences ? Contactez-nous
            sur{' '}
          </span>
          <a href={`mailto:${import.meta.env.VITE_CONTACT_ADDRESS}`}>
            {import.meta.env.VITE_CONTACT_ADDRESS}
          </a>
        </Contact>
      )}
      <ReassuranceItems />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Title = styled.h2`
  font-family: 'brother-1816';
  font-size: 2.25rem;
  font-weight: bold;
  color: ${colors.midnightBlue};
  text-align: center;
`

const DiscountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
`

const Discount = styled.p`
  margin: 0 0 1rem;
  font-family: Montserrat;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: normal;
  line-height: 2.25rem;
  color: ${colors.midnightBlue};
  text-align: center;
`

const SubscriptionsList = styled.ul<{ $hasBanner: boolean }>`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: ${({ $hasBanner }) => ($hasBanner ? 'center' : 'stretch')};
  justify-content: center;
  margin-bottom: 4.375rem;

  & > li {
    display: flex;
    margin-top: 2rem;
    margin-right: 1.6875rem;
    list-style-type: none;
  }
`

const Contact = styled.div`
  font-family: Montserrat;
  font-size: 1.125rem;
  color: ${colors.midnightBlue};
  text-align: center;
  margin-bottom: 4.375rem;

  & a {
    margin-top: 4.375rem;
  }
`

const SwitchWrapper = styled.div`
  display: flex;
  width: 25rem;
  justify-content: space-between;
  font-family: Montserrat;
  font-size: 1.15rem;
  font-weight: bold;
  color: ${colors.midnightBlue};
`

export default Subscriptions
